import React, { useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { Link } from '@americanexpress/dls-react';
import { cn, formatText } from '@/helpers/utils';
import { useRouteParams } from '@/context/routeParamsContext';
import { useTranslatedAirportLounges } from '@/hooks/loungesHooks/loungesHelperHooks';
import {
  useOneCmsLocaleResultsObj,
  useCommonContent,
} from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { useRouter, useRouterLocation } from '@americanexpress/one-app-router';
import {
  queryToFilters,
  filtersToQuery,
  filterLounges,
  hasFilters,
} from '@/helpers/airportFiltersHelper';
import { LoungeTypes } from '@/constants';
import AirportFilters from './AirportFilters';
import RouteReplaceWithQueryParams from '../common/RouteReplaceWithQueryParams';
import CardCalloutNotification from '../common/CardCalloutNotification';
import InlineCardCalloutNotification from '../common/InlineCardCalloutNotification';
import Loading from '../common/Loading';
import SearchLoungeResult from '../common/SearchLoungeResult';
// import SearchLoungeNoResult from './SearchLoungeNoResult';

const SearchLoungesByAirportList = () => {
  const router = useRouter();
  const location = useRouterLocation();

  const appliedFilters = queryToFilters(location.search);
  const [localFilters, setLocalFilters] = React.useState(appliedFilters);

  useEffect(() => {
    setLocalFilters(queryToFilters(location.search));
  }, [location.search]);

  const resultsCopy = useOneCmsLocaleResultsObj();
  const { product, airportCode } = useRouteParams();
  const { data, error, isLoading } = useTranslatedAirportLounges();
  const {
    pageTitles: { airport },
    productDisplayNames,
    loungeFilters,
    config: { features = [] },
  } = useCommonContent();

  const isFiltersEnabled = features.some(
    (feat) => feat.name === 'loungeFilters' && feat.enabled
  );
  const isSidecarEnabled =
    features.some((feat) => feat.name === 'sidecar' && feat.enabled) &&
    !product?.includes('idc');

  const lounges = useMemo(() => {
    const experienceRank = {
      [LoungeTypes.CENTURION]: 0,
      [LoungeTypes.SIDECAR]: 1,
      [LoungeTypes.PARTNER]: 2,
    };

    return (data || [])
      .filter(Boolean)
      .filter(
        (lounge) =>
          lounge.experience_type !== LoungeTypes.SIDECAR || isSidecarEnabled
      )
      .sort((a, b) => {
        const terminalCompare = a.location.terminal.localeCompare(
          b.location.terminal
        );
        if (terminalCompare !== 0) return terminalCompare;

        const firstLoungeRank = experienceRank[a?.experienceType];
        const secondLoungeRank = experienceRank[b?.experienceType];
        if (firstLoungeRank !== undefined && secondLoungeRank !== undefined) {
          return firstLoungeRank - secondLoungeRank;
        }
        return 0;
      });
  }, [data, isSidecarEnabled]);

  const filteredLounges = useMemo(() => {
    if (!isFiltersEnabled) return lounges;
    return filterLounges(lounges, appliedFilters);
  }, [lounges, appliedFilters, isFiltersEnabled]);

  // Validate airportCode from route
  const isValidAirportCode = /^[A-Za-z]{3}$/.test(airportCode);
  if (error || !isValidAirportCode) {
    return <RouteReplaceWithQueryParams to={`/${product}/`} />;
  }

  // if (error || (!isLoading && lounges.length === 0)) {
  //   return <SearchLoungeNoResult />;
  // }

  if (isLoading) {
    return <Loading containerClassName="tw-m-12 tw-flex tw-justify-center" />;
  }

  if (lounges.length === 0) {
    return (
      <div className="tw-border-b tw-border-gray-300 tw-p-4">
        <p>Sorry, there are no lounges available for this airport</p>
      </div>
    );
  }

  const { name: airportName, iata } = lounges[0].airport;
  const numCopy =
    filteredLounges.length === 1
      ? loungeFilters?.results?.singular
      : loungeFilters?.results?.multiple;

  // for meta description
  const productName = productDisplayNames[product]?.displayName?.replace(
    'The ',
    ''
  );

  const applyFiltersToUrl = (filters = localFilters) => {
    const queryString = filtersToQuery(filters);

    const url = queryString
      ? `${location?.pathname}?${queryString}`
      : location?.pathname;
    router.push(url);
  };

  const hasAppliedFilters = hasFilters(appliedFilters);

  return (
    <>
      <Helmet>
        <title>{formatText(airport?.title, { airportName })}</title>
        <meta
          name="description"
          content={formatText(airport?.description, {
            airportName,
            productName,
          })}
        />
      </Helmet>
      <div className="tw-mb-6 lg_tw-flex">
        <h1 className="tw-heading-lg-book tw-mr-2 tw-text-gray-900 md_tw-heading-display-small-book">
          {formatText(resultsCopy.loungesAt, { airportName, iata })}
        </h1>
        {!isFiltersEnabled && (
          <p className="tw-heading-xs-bold tw-mt-2 tw-flex-shrink-0 tw-text-gray-700 lg_tw-ml-auto lg_tw-mt-auto">
            {formatText(
              filteredLounges.length > 1
                ? resultsCopy?.multiple
                : resultsCopy?.singular,
              { length: filteredLounges.length }
            )}
          </p>
        )}
      </div>

      {isFiltersEnabled && (
        <AirportFilters
          lounges={lounges}
          localFilters={localFilters}
          appliedFilters={appliedFilters}
          setLocalFilters={setLocalFilters}
          applyFilters={applyFiltersToUrl}
        />
      )}

      {isFiltersEnabled ? (
        <div className="tw-mb-10 tw-flex tw-flex-col tw-gap-2 tw-border-b tw-border-gray-300 md_tw-flex-row">
          <p className="tw-heading-xs tw-text-gray-700">
            {numCopy &&
              `${formatText(numCopy, { length: filteredLounges.length })}`}
          </p>
          <InlineCardCalloutNotification className="tw-mb-10" />
        </div>
      ) : (
        <CardCalloutNotification className="tw-mb-10" />
      )}
      {filteredLounges.length === 0 &&
        hasAppliedFilters &&
        isFiltersEnabled && (
          <div className="tw-flex tw-flex-col tw-items-center tw-px-6 tw-py-10 md_tw-px-28">
            <div className="tw-text-2xl tw-font-normal tw-text-gray-900">
              {loungeFilters?.noLounges?.title}
            </div>
            <p className="tw-text-center tw-text-base tw-font-normal tw-text-gray-700">
              {loungeFilters?.noLounges?.description}
            </p>
            <div className="tw-mt-2">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- reset filters */}
              <Link
                isInline
                className="tw-mr-1 tw-flex-shrink-0"
                onClickCapture={(e) => {
                  // Prevent child handlers from hijacking tracking info
                  // to resolve issue where tracked events show generic
                  // html element rather than the provided element/detail
                  e.stopPropagation();
                  e.preventDefault();
                  applyFiltersToUrl({});
                }}
              >
                {loungeFilters?.actions?.resetFilters}
              </Link>
              <span className="tw-text-base tw-font-normal tw-text-gray-700">
                {loungeFilters?.noLounges?.seeMoreOptionsText}
              </span>
            </div>
          </div>
        )}

      <style>
        {/* override carousel control button iconButton */}
        {`
          .carousel-override button > :first-child {
            height: 2.75rem;
          }
        `}
      </style>
      <div className="tw-relative tw-grid tw-grid-cols-1 tw-place-items-center tw-gap-x-8 tw-gap-y-6 sm_tw-grid-cols-2 lg_tw-grid-cols-3">
        {filteredLounges.flatMap((lounge, i) => [
          (i === 0 ||
            lounge.location.terminal !==
              filteredLounges[i - 1].location.terminal) && (
            <h2
              key={lounge.location.terminal}
              className={cn(
                'tw-heading-md-regular tw-justify-self-start tw-text-gray-900 sm_tw-col-span-2 lg_tw-col-span-3',
                { 'lg_tw-mt-4': i > 0 }
              )}
            >
              {lounge.location.terminal}
            </h2>
          ),
          <SearchLoungeResult
            lounge={lounge}
            key={lounge.id}
            headingLevel={3}
          />,
        ])}
      </div>
    </>
  );
};

SearchLoungesByAirportList.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
};

export default SearchLoungesByAirportList;
